import { ReactNode } from "react";
import { TableCard } from "@/shared/components/tables/table-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ListRequest,
  useCustomersServiceObjectListQuery,
} from "@/services/backend/customers/api/service-object-api";
import t from "@/lang/lang";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { oneLine } from "@/services/backend/addresses/types/address";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { Button } from "@/shared/components/ui/button";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";

export function ServiceObjectsTable({
  request,
  onRequestChange,
  renderRowLink,
  hideBorder = false,
  hidePagination = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (
    customerId: string,
    serviceObjectId: string,
    children: ReactNode,
  ) => ReactNode;
  hideBorder?: boolean;
  hidePagination?: boolean;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useCustomersServiceObjectListQuery(request);

  const emptyList = !isLoading && !error && list!.serviceObjects.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Name")}</TableHead>
              <TableHead>{t("Kunde")}</TableHead>
              <TableHead>{t("Adresse")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={3} />}
            {error && <TableRtkErrorRow error={error} colSpan={3} />}
            {emptyList && <TableEmptyRow colSpan={3} />}
            {list?.serviceObjects.map((serviceObject) => (
              <TableRow key={serviceObject.id}>
                <TableCell className="py-0">
                  {renderRowLink(
                    serviceObject.customerId,
                    serviceObject.id,
                    <Button variant="link">{serviceObject.name}</Button>,
                  )}
                </TableCell>
                <TableCell>
                  <CustomerName customerId={serviceObject.customerId} />
                </TableCell>
                <TableCell>{oneLine(serviceObject.address)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) => {
            onRequestChange({ ...request, limit, offset });
          }}
          className="ml-auto"
        />
      )}
    </div>
  );
}

export function ServiceObjectsTableFilters({
  request,
  onRequestChange,
  hideCustomer = false,
  hideName = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  hideCustomer?: boolean;
  hideName?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {!hideCustomer && (
        <CustomerFilter
          filter={request.customer ?? { active: false, values: [] }}
          onFilterChange={(customer) =>
            onRequestChange({ ...request, customer })
          }
        />
      )}
      {!hideName && (
        <SearchFilter
          filter={request.name || { active: false, values: [] }}
          onFilterChange={(name) => onRequestChange({ ...request, name })}
          placeholder={t("Nach Name suchen ...")}
        />
      )}
    </div>
  );
}
